import React from 'react';
import { Button, Card, Col, Input, Modal, Row, Space, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import TagInput from '../widgets/TagInput';
import InferenceConfidenceBadge from './InferenceConfidenceBadge';

const { Text, Title } = Typography;
const { TextArea } = Input;

const metricCardStyle = {
  borderRadius: 999,
  fontWeight: 600,
  paddingInline: 14,
  paddingBlock: 4,
  marginInlineEnd: 0,
};

function ProgressMini({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 108 }}>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: current >= 1 ? '#2563eb' : '#e5e7eb', color: current >= 1 ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10 }}>1</div>
      <div style={{ height: 3, flex: 1, background: '#dbe3ef', borderRadius: 999, position: 'relative', overflow: 'hidden' }}>
        <div style={{ width: current === 2 ? '100%' : '45%', height: '100%', background: '#2563eb' }} />
      </div>
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: current >= 2 ? '#2563eb' : '#e5e7eb', color: current >= 2 ? '#fff' : '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 10 }}>2</div>
    </div>
  );
}

export default function FaqEnrichmentReviewModal({
  visible,
  faqDrafts,
  onChange,
  onBack,
  onNext,
  onSaveDraft,
}) {
  const updateItem = (id, patch) => {
    onChange(
      faqDrafts.map((item) =>
        item.id === id ? { ...item, ...patch, editedByUser: true } : item,
      ),
    );
  };

  const summary = {
    total: faqDrafts.length,
    merge: faqDrafts.filter((item) => item.suggestionType === 'merge').length,
    drop: faqDrafts.filter((item) => item.suggestionType === 'drop').length,
    low: faqDrafts.filter((item) => item.confidence === 'low').length,
  };

  return (
    <Modal
      open={visible}
      onCancel={onBack}
      footer={null}
      width={1320}
      title={null}
      centered
      destroyOnHidden
      styles={{ body: { padding: 0 }, content: { borderRadius: 20, overflow: 'hidden' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '82vh' }}>
        <div style={{ padding: '10px 18px', borderBottom: '1px solid #eef2f7', background: '#fff' }}>
          <Space direction="vertical" size={10} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Bước 1 / 2</Text>
                  <Title level={4} style={{ margin: 0, lineHeight: 1.15 }}>Duyệt FAQ đã làm giàu</Title>
                </div>
                <Text type="secondary" style={{ display: 'block', marginTop: 2, fontSize: 13 }}>
                  Kiểm tra FAQ AI đã chuẩn hoá và gắn độ tin cậy trước khi sinh cấu hình Agent.
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: '#f8fafc', border: '1px solid #e5e7eb', flexShrink: 0 }}>
                <Text type="secondary" style={{ fontSize: 11, margin: 0 }}>Tiến trình</Text>
                <ProgressMini current={1} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <Space wrap size={10}>
                <Tag color="blue" style={metricCardStyle}>Tổng FAQ: {summary.total}</Tag>
                <Tag color="purple" style={metricCardStyle}>Đề xuất gộp: {summary.merge}</Tag>
                <Tag color="red" style={metricCardStyle}>Đề xuất bỏ: {summary.drop}</Tag>
                <Tag color="orange" style={metricCardStyle}>Suy luận thấp: {summary.low}</Tag>
              </Space>

              <div style={{ marginLeft: 'auto', minWidth: 280 }}>
                <Input prefix={<SearchOutlined />} placeholder="Tìm theo câu hỏi, tag..." />
              </div>
            </div>

            <Space wrap size={10}>
              <Tag style={{ ...metricCardStyle, background: '#eef4ff', color: '#2563eb', borderColor: '#bfdbfe' }}>Tất cả</Tag>
              <Tag style={{ ...metricCardStyle, background: '#faf5ff', color: '#7c3aed', borderColor: '#ddd6fe' }}>Đề xuất gộp</Tag>
              <Tag style={{ ...metricCardStyle, background: '#fff7ed', color: '#ea580c', borderColor: '#fed7aa' }}>Đề xuất bỏ</Tag>
              <Tag style={{ ...metricCardStyle, background: '#fffbea', color: '#ca8a04', borderColor: '#fde68a' }}>Suy luận thấp</Tag>
            </Space>
          </Space>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 24px', background: '#fafcff' }}>
          <Space direction="vertical" size={14} style={{ width: '100%' }}>
            {faqDrafts.map((item, index) => (
              <Card
                key={item.id || index}
                size="small"
                bodyStyle={{ padding: 18 }}
                style={{ borderRadius: 18, borderColor: '#e5e7eb', boxShadow: '0 4px 16px rgba(15,23,42,0.04)' }}
              >
                <Space direction="vertical" size={14} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' }}>
                    <div>
                      <Text type="secondary">FAQ #{index + 1}</Text>
                      <Title level={4} style={{ margin: '6px 0 0', lineHeight: 1.25 }}>{item.question}</Title>
                    </div>
                    <InferenceConfidenceBadge
                      value={item.confidence}
                      suggestionType={item.suggestionType}
                      compact
                    />
                  </div>

                  <Input
                    value={item.question}
                    onChange={(event) => updateItem(item.id, { question: event.target.value })}
                    placeholder="Câu hỏi chuẩn hoá"
                    size="large"
                  />

                  <TextArea
                    rows={4}
                    value={item.answer}
                    onChange={(event) => updateItem(item.id, { answer: event.target.value })}
                    placeholder="Câu trả lời chuẩn hoá"
                    style={{ lineHeight: 1.7 }}
                  />

                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>Biến thể câu hỏi</Text>
                    <TagInput
                      tags={item.questionVariants || []}
                      onChange={(value) => updateItem(item.id, { questionVariants: value })}
                      placeholder="Thêm biến thể"
                    />
                  </div>

                  <Row gutter={24} align="bottom">
                    <Col span={18}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>Tags</Text>
                      <TagInput
                        tags={item.tags || []}
                        onChange={(value) => updateItem(item.id, { tags: value })}
                        placeholder="Thêm tag"
                      />
                    </Col>
                    <Col span={6}>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>Priority</Text>
                      <Input
                        value={item.priority}
                        onChange={(event) => updateItem(item.id, { priority: Number(event.target.value || 0) })}
                      />
                    </Col>
                  </Row>

                  {item.suggestionReason ? (
                    <div style={{ padding: '12px 14px', borderRadius: 12, background: '#f8fafc', border: '1px solid #e5e7eb' }}>
                      <Text type="secondary">Lý do AI: {item.suggestionReason}</Text>
                    </div>
                  ) : null}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 2 }}>
                    <Button onClick={() => updateItem(item.id, { suggestionType: 'keep' })}>Giữ lại</Button>
                    <Button onClick={() => updateItem(item.id, { suggestionType: 'merge' })}>Đề xuất gộp</Button>
                    <Button danger onClick={() => updateItem(item.id, { suggestionType: 'drop' })}>Đề xuất bỏ</Button>
                  </div>
                </Space>
              </Card>
            ))}
          </Space>
        </div>

        <div style={{ padding: '12px 0px 0px', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(8px)', borderTop: '1px solid #e5e7eb' }}>
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 18, padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>Trước khi sang bước 2</Text>
              <Text type="secondary">Bạn có thể quay lại bước parse thô nếu thấy dữ liệu nguồn cần chỉnh thêm.</Text>
            </div>
            <Space>
              <Button onClick={onBack}>Quay lại chỉnh parse thô</Button>
              <Button onClick={onSaveDraft}>Lưu nháp</Button>
              <Button type="primary" onClick={onNext}>Tiếp tục sang cấu hình Agent</Button>
            </Space>
          </div>
        </div>
      </div>
    </Modal>
  );
}
